import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ImageUpload from "@/components/ImageUpload";
import SquareSettingsCard from "@/components/SquareSettingsCard";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { CLINIC } from "@/constants/testIds";

const TIMEZONES = ["America/Chicago", "America/New_York", "America/Denver", "America/Los_Angeles"];

/** Configuración: datos de la academia y conexion con Square. */
export default function SettingsPage() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    api.get("/clinic").then(({ data }) => setForm(data));
  }, []);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/clinic", {
        name: form.name,
        address: form.address || null,
        phone: form.phone || null,
        email: form.email || null,
        whatsapp_number: form.whatsapp_number || null,
        timezone: form.timezone,
        logo_url: form.logo_url,
      });
      setForm(data);
      toast({ title: "Configuración guardada" });
    } catch (err) {
      toast({ title: "Error al guardar", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <p className="text-center text-muted-foreground py-12">Cargando configuración...</p>;

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-foreground">Configuración</h1>
        <p className="text-base text-muted-foreground mt-1">Datos de la academia y cobros con tarjeta.</p>
      </div>

      <div className="bg-white border border-border rounded-xl p-6 sm:p-8 shadow-sm space-y-5">
        <div>
          <Label className="text-sm text-muted-foreground mb-1 block">Logo</Label>
          <ImageUpload currentUrl={form.logo_url} onUploaded={(url) => setForm((f) => ({ ...f, logo_url: url }))} placeholderUrl={null} testId={CLINIC.logoUpload} shape="square" />
        </div>

        <div>
          <Label className="text-sm text-muted-foreground mb-1 block">Nombre de la academia</Label>
          <Input value={form.name} onChange={set("name")} data-testid={CLINIC.nameInput} />
        </div>

        <div>
          <Label className="text-sm text-muted-foreground mb-1 block">Dirección</Label>
          <Input value={form.address || ""} onChange={set("address")} data-testid={CLINIC.addressInput} placeholder="Houston, TX, USA" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Teléfono</Label>
            <Input value={form.phone || ""} onChange={set("phone")} data-testid={CLINIC.phoneInput} />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Email de contacto</Label>
            <Input type="email" value={form.email || ""} onChange={set("email")} data-testid={CLINIC.emailInput} />
          </div>
        </div>

        <div>
          <Label className="text-sm text-muted-foreground mb-1 block">WhatsApp de contacto</Label>
          <Input value={form.whatsapp_number || ""} onChange={set("whatsapp_number")} placeholder="+1 713 000 0000" data-testid={CLINIC.whatsappInput} />
          <p className="text-xs text-muted-foreground mt-1">Se muestra al alumno en su página de inscripción.</p>
        </div>

        <div>
          <Label className="text-sm text-muted-foreground mb-1 block">Zona horaria</Label>
          <Select value={form.timezone} onValueChange={(v) => setForm((f) => ({ ...f, timezone: v }))}>
            <SelectTrigger data-testid={CLINIC.timezoneSelect}><SelectValue /></SelectTrigger>
            <SelectContent>
              {TIMEZONES.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="pt-2">
          <Button onClick={handleSave} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid={CLINIC.saveButton}>
            {saving ? "Guardando..." : "Guardar cambios"}
          </Button>
        </div>
      </div>

      <SquareSettingsCard />
    </div>
  );
}
